import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { Sparkle, ArrowCounterClockwise, Warning, ChartLine, BookmarkSimple, X, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) =>
  `${n < 0 ? "-" : ""}£${Math.abs(Math.round(n)).toLocaleString("en-GB")}`;

const SCENARIOS_KEY = "forecastly_whatif_scenarios_v1";
const HANDOFF_KEY = "forecastly_whatif_handoff_v1";
const MAX_SCENARIOS = 3;

const Slider = ({ label, value, onChange, min, max, step, unit, hint }) => (
  <div className="space-y-2">
    <div className="flex items-baseline justify-between gap-3">
      <label className="text-sm font-semibold uppercase tracking-wide">{label}</label>
      <div className="font-heading text-2xl tabular-nums">
        {value > 0 ? "+" : ""}
        {value}
        {unit}
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-black cursor-pointer"
      data-testid={`whatif-slider-${label.toLowerCase().replace(/\s/g, "-")}`}
    />
    <p className="text-xs text-black/60 leading-snug">{hint}</p>
  </div>
);

/**
 * Lightweight sensitivity widget — three sliders that shift the forecast in-place.
 * Pure client-side math: no API calls, no DB writes. Sliders reset on refresh.
 *
 * Inputs:
 *   - months: the forecast.months_preview array as returned by /api/dashboard
 *   - openingCash: starting balance to walk the cash chain from
 */
export default function WhatIfSlider({ months, openingCash = 0 }) {
  const [revenuePct, setRevenuePct] = useState(0); // -50 .. +50
  const [costsPct, setCostsPct] = useState(0); // -50 .. +50
  const [delayDays, setDelayDays] = useState(0); // 0 .. 60

  // Saved scenarios live in localStorage so they survive refresh but never reach
  // our backend (keeping the widget truly stateless on the server).
  const [savedScenarios, setSavedScenarios] = useState(() => {
    try {
      const raw = window.localStorage.getItem(SCENARIOS_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SCENARIOS_KEY, JSON.stringify(savedScenarios));
    } catch {
      // QuotaExceeded or disabled localStorage — silently no-op, scenarios
      // simply won't persist past this session. Never block the UI.
    }
  }, [savedScenarios]);

  // Big-Decision handoff from Goals page. When the user clicks "Yes, show me
  // the impact" we get {goal_title, monthly_cost_adjustment} in localStorage —
  // convert the absolute £/month into a costs% shift based on the user's
  // average monthly costs in the forecast, then pre-load the slider.
  const [handoff, setHandoff] = useState(null);
  useEffect(() => {
    if (!months || months.length === 0) return;
    let payload = null;
    try {
      const raw = window.localStorage.getItem(HANDOFF_KEY);
      payload = raw ? JSON.parse(raw) : null;
    } catch {
      payload = null;
    }
    if (!payload || !payload.monthly_cost_adjustment) return;
    // Average monthly cost over the forecast horizon (including personal draw).
    const avgMonthlyCosts =
      months.reduce(
        (sum, m) => sum + ((m.total_costs || 0) + (m.personal_draw || 0)),
        0,
      ) / months.length;
    if (avgMonthlyCosts <= 0) return;
    const pctUplift = Math.round(
      (Number(payload.monthly_cost_adjustment) / avgMonthlyCosts) * 100,
    );
    // Cap to slider bounds (-50..+50) but keep the original £ on the chip
    // so the label stays meaningful.
    const clamped = Math.max(-50, Math.min(50, pctUplift));
    setCostsPct(clamped);
    setRevenuePct(0);
    setDelayDays(0);
    setHandoff(payload);
  }, [months]);

  const exitModelling = () => {
    try {
      window.localStorage.removeItem(HANDOFF_KEY);
    } catch {
      // ignore
    }
    setHandoff(null);
    setRevenuePct(0);
    setCostsPct(0);
    setDelayDays(0);
  };

  const save = () => {
    if (savedScenarios.length >= MAX_SCENARIOS) return;
    if (revenuePct === 0 && costsPct === 0 && delayDays === 0) {
      toast.error("Move the sliders first — saving a blank scenario won't help much.");
      return;
    }
    const parts = [];
    if (revenuePct !== 0) parts.push(`Rev ${revenuePct > 0 ? "+" : ""}${revenuePct}%`);
    if (costsPct !== 0) parts.push(`Costs ${costsPct > 0 ? "+" : ""}${costsPct}%`);
    if (delayDays !== 0) parts.push(`Delay ${delayDays}d`);
    const name = parts.join(" · ");
    setSavedScenarios([
      ...savedScenarios,
      { id: String(Date.now()), name, revenuePct, costsPct, delayDays },
    ]);
    toast.success("Scenario saved.");
  };

  const loadScenario = (s) => {
    setRevenuePct(s.revenuePct);
    setCostsPct(s.costsPct);
    setDelayDays(s.delayDays);
  };

  const deleteScenario = (id) => {
    setSavedScenarios(savedScenarios.filter((s) => s.id !== id));
  };

  // Sensitivity-shifted monthly series:
  //   - revenuePct scales the forecast income up/down
  //   - costsPct scales costs + personal_draw up/down
  //   - delayDays approximates late-paying customers by shifting `delayDays/30` of
  //     this month's income to next month. (cap at 100% shift)
  const projected = useMemo(() => {
    if (!months || months.length === 0) return [];
    const rev = 1 + revenuePct / 100;
    const cost = 1 + costsPct / 100;
    const shiftRatio = Math.min(1, delayDays / 30);

    // Phase 1: apply scaling per month
    const scaled = months.map((m) => ({
      ...m,
      _scaled_income: (m.total_income || 0) * rev,
      _scaled_costs: ((m.total_costs || 0) + (m.personal_draw || 0)) * cost,
    }));
    // Phase 2: redistribute income by delay (push portion of m[i] to m[i+1])
    const redistributed = scaled.map((m, i) => {
      const heldBack = m._scaled_income * shiftRatio;
      const fromPrev = i > 0 ? scaled[i - 1]._scaled_income * shiftRatio : 0;
      const adjustedIncome = m._scaled_income - heldBack + fromPrev;
      return {
        label: m.label?.split(" ")[0] || "",
        baseCash: m.cash_balance, // unchanged baseline for comparison
        adjustedIncome,
        adjustedCosts: m._scaled_costs,
        net: adjustedIncome - m._scaled_costs,
      };
    });
    // Phase 3: walk a fresh cash chain. Preserve the original full month label
    // (e.g. "June 2026") so we can call out which month is the worst.
    let cash = openingCash;
    return redistributed.map((m, i) => {
      cash += m.net;
      return { ...m, fullLabel: months[i]?.label || "", adjustedCash: cash };
    });
  }, [months, openingCash, revenuePct, costsPct, delayDays]);

  const monthsBelowZero = projected.filter((m) => m.adjustedCash < 0).length;
  const baseFinal = months?.[months.length - 1]?.cash_balance ?? 0;
  const adjFinal = projected[projected.length - 1]?.adjustedCash ?? 0;
  const delta = adjFinal - baseFinal;
  // Worst single month = lowest closing cash. Surface its label for the KPI card.
  const worstMonth = projected.reduce(
    (acc, m) => (m.adjustedCash < acc.adjustedCash ? m : acc),
    projected[0] || { adjustedCash: 0, fullLabel: "" }
  );

  const reset = () => {
    setRevenuePct(0);
    setCostsPct(0);
    setDelayDays(0);
  };

  if (!months || months.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="card"
      data-testid="whatif-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h2 className="font-heading uppercase text-2xl flex items-center gap-2">
            <Sparkle size={22} weight="duotone" /> What if…?
          </h2>
          <p className="mt-2 text-sm text-black/70">
            Drag the sliders to stress-test your forecast in real-time. Could you survive
            a quiet quarter? A 15% cost jump? A wave of late payers?
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary whitespace-nowrap"
            onClick={save}
            disabled={savedScenarios.length >= 3}
            data-testid="whatif-save-btn"
            title={savedScenarios.length >= 3 ? "You've already saved 3 scenarios — delete one first." : "Bookmark this stress test"}
          >
            <BookmarkSimple size={14} weight="bold" /> Save this What-If
          </button>
          <button
            type="button"
            className="btn-secondary"
            onClick={reset}
            data-testid="whatif-reset-btn"
          >
            <ArrowCounterClockwise size={14} weight="bold" /> Reset
          </button>
        </div>
      </div>

      {/* Big-Decision modelling banner — surfaces when handoff is active. */}
      {handoff && (
        <div
          className="mt-4 flex items-start gap-3 rounded-2xl bg-[#FFF7E5] border-[1.5px] border-black p-4"
          data-testid="whatif-modelling-banner"
        >
          <Briefcase size={20} weight="duotone" className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider font-semibold text-black/70">
              Modelling a big decision
            </div>
            <p className="text-sm mt-1 leading-relaxed">
              We've pre-loaded the cost slider to show the impact of{" "}
              <strong>"{handoff.goal_title}"</strong> — roughly{" "}
              <strong>£{Math.round(Number(handoff.monthly_cost_adjustment)).toLocaleString("en-GB")}/month</strong>{" "}
              extra. Drag the sliders to layer on other what-ifs, or exit modelling to clear.
            </p>
          </div>
          <button
            type="button"
            onClick={exitModelling}
            className="btn-secondary !px-3 !py-2 shrink-0"
            data-testid="whatif-exit-modelling-btn"
            title="Exit modelling and clear sliders"
          >
            <X size={14} weight="bold" /> Exit modelling
          </button>
        </div>
      )}

      {/* Saved scenarios row */}
      {savedScenarios.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="whatif-saved-row">
          <span className="text-xs uppercase tracking-wider font-semibold text-black/60 mr-1">
            Saved:
          </span>
          {savedScenarios.map((s) => (
            <div
              key={s.id}
              className="inline-flex items-center gap-1.5 border-[1.5px] border-black rounded-full bg-[#FFBD4A] pl-3 pr-1 py-0.5 text-xs font-semibold"
            >
              <button
                type="button"
                className="hover:underline"
                onClick={() => loadScenario(s)}
                data-testid={`whatif-load-${s.id}`}
              >
                {s.name}
              </button>
              <button
                type="button"
                className="rounded-full p-1 hover:bg-black/10"
                onClick={() => deleteScenario(s.id)}
                data-testid={`whatif-delete-${s.id}`}
                aria-label="Delete scenario"
              >
                <X size={11} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 grid md:grid-cols-3 gap-6">
        <Slider
          label="Revenue"
          value={revenuePct}
          onChange={setRevenuePct}
          min={-50}
          max={50}
          step={5}
          unit="%"
          hint="What if every month's income shifted up or down?"
        />
        <Slider
          label="Costs"
          value={costsPct}
          onChange={setCostsPct}
          min={-50}
          max={50}
          step={5}
          unit="%"
          hint="Higher supplier prices, your pay rise, or trimmed costs."
        />
        <Slider
          label="Payment delay"
          value={delayDays}
          onChange={setDelayDays}
          min={0}
          max={60}
          step={5}
          unit=" days"
          hint="Late-paying customers — how late, on average?"
        />
      </div>

      {/* Summary band */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <div className="rounded-2xl border-[1.5px] border-black p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-black/65">
            Year-end cash
          </div>
          <div className="font-heading text-3xl mt-1">{fmt(adjFinal)}</div>
          <div
            className={`mt-1 text-xs font-semibold ${
              delta < 0 ? "text-black" : "text-[#1B7E3C]"
            }`}
          >
            {delta >= 0 ? "▲" : "▼"} {fmt(Math.abs(delta))} vs forecast
          </div>
        </div>
        <div
          className={`rounded-2xl border-[1.5px] border-black p-4 ${
            monthsBelowZero > 0 ? "bg-[#FFBD4A]" : ""
          }`}
          data-testid="whatif-runway-card"
        >
          <div className="text-xs uppercase tracking-wider font-semibold text-black/75">
            Months in the red
          </div>
          <div className="font-heading text-3xl mt-1 flex items-center gap-2">
            {monthsBelowZero}
            {monthsBelowZero > 0 && <Warning size={20} weight="fill" />}
          </div>
          <div className="mt-1 text-xs">
            {monthsBelowZero === 0
              ? "You'd hold the line — no negative balances."
              : monthsBelowZero === 1
              ? "One month dips below zero. Plan a buffer."
              : `${monthsBelowZero} months below zero. Bigger buffer needed.`}
          </div>
        </div>
        <div className="rounded-2xl border-[1.5px] border-black p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-black/65">
            Worst single month{worstMonth?.fullLabel ? <> — <strong className="text-black">{worstMonth.fullLabel}</strong></> : ""}
          </div>
          <div className="font-heading text-3xl mt-1">
            {fmt(worstMonth?.adjustedCash ?? 0)}
          </div>
          <div className="mt-1 text-xs text-black/65">Lowest closing cash in the year</div>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-6 h-64 min-h-[220px] w-full">
        {projected.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" minHeight={220} debounce={50}>
          <LineChart data={projected} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
            <XAxis dataKey="label" stroke="#000" tick={{ fontFamily: "Open Sans", fontSize: 12 }} />
            <YAxis
              stroke="#000"
              tick={{ fontFamily: "Open Sans", fontSize: 12 }}
              tickFormatter={(v) => `£${Math.round(v / 1000)}k`}
            />
            <Tooltip
              contentStyle={{
                border: "1.5px solid #000",
                borderRadius: 8,
                fontFamily: "Open Sans",
              }}
              formatter={(v) => fmt(v)}
            />
            <Legend wrapperStyle={{ fontFamily: "Open Sans", fontSize: 12 }} />
            <ReferenceLine y={0} stroke="#000" strokeDasharray="2 2" />
            <Line
              type="monotone"
              dataKey="baseCash"
              name="Original forecast"
              stroke="rgba(0,0,0,0.45)"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="adjustedCash"
              name="What-if scenario"
              stroke="#FFBD4A"
              strokeWidth={3}
              dot={{ stroke: "#000", strokeWidth: 1.5, r: 3, fill: "#FFBD4A" }}
            />
          </LineChart>
        </ResponsiveContainer>
        )}
      </div>

      <p className="mt-4 text-xs text-black/55 flex items-start gap-1.5 leading-relaxed">
        <ChartLine size={12} weight="bold" className="mt-0.5 shrink-0" />
        <span>
          This is a stress test, not a scenario builder. Sliders reset when you leave the page —
          your real forecast is untouched. You can save a specific stress-test above and revisit
          it at any point. Full scenario building functionality will be added in future releases.
        </span>
      </p>
    </motion.div>
  );
}
